import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateConfig } from "../../../../store/slices/Master/DesignBank/designBankSlice";
import { addDesignBankData, editDesignBankData } from "../api";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { useGetDesignBankBasicDataQuery } from "../../../../store/queries/Master";

const useAddDesignBank = ({ refetch }) => {
  const [imagePreview, setImagePreview] = useState("");
  const dispatch = useDispatch();
  const queryState = useSelector((state) => state.designBank);
  const { data: basicData = {} } = useGetDesignBankBasicDataQuery();
  const supplierOptions = basicData?.data?.suppliers ?? [];
  const itemTypeOptions = basicData?.data?.item_types ?? [];

  const initialValues = useMemo(() => {
    if (!queryState?.isEdit || !queryState?.itemData) {
      return {
        design_id: "",
        supplier: "",
        item_type: "",
        image_url: "",
      };
    }

    const supplierId =
      queryState?.itemData?.supplier?._id ||
      queryState?.itemData?.supplier_id ||
      queryState?.itemData?.supplier ||
      "";
    const itemTypeId =
      queryState?.itemData?.item_type?._id ||
      queryState?.itemData?.item_id ||
      queryState?.itemData?.item_type ||
      "";

    return {
      design_id: queryState?.itemData?.design_id || "",
      supplier:
        supplierOptions?.find((item) => item?._id === supplierId) || "",
      item_type:
        itemTypeOptions?.find((item) => item?._id === itemTypeId) || "",
      image_url: queryState?.itemData?.image_url || "",
    };
  }, [
    itemTypeOptions,
    queryState?.isEdit,
    queryState?.itemData,
    supplierOptions,
  ]);

  const validation = Yup.object({
    design_id: Yup.string().required("Enter design id"),
    supplier: Yup.object().required("Select supplier"),
    item_type: Yup.object().required("Select item type"),
    image_url: Yup.mixed().required("Select an image"),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validation,
    onSubmit: (values) => {
      if (queryState?.isEdit) {
        const formData = new FormData();
        formData.append("design_bank_id", queryState?.selectedItem);
        formData.append("supplier_id", values?.supplier?._id || "");
        formData.append("item_id", values?.item_type?._id || "");
        // Only append image if it's a new File (i.e., user uploaded/changed it)
        if (values?.image_url instanceof File) {
          formData.append("image", values.image_url);
        }

        editDesignBankData(formData).then((response) => {
          if (response?.data?.status_code === 200) {
            handleCloseModal();
            toast.success("Added successfully");
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.selectedItem = "";
              })
            );
            formik?.setFieldValue("image_url", "");
            formik?.resetForm();
            refetch();
          } else if (response?.status_code === 422) {
            handleBackendError(response);
          } else {
            toast.error("Something went wrong");
            handleBackendError(response);
          }
        });
      } else {
        const formData = new FormData();
        formData.append("design_id", values.design_id);
        formData.append("supplier_id", values?.supplier?._id || "");
        formData.append("item_id", values?.item_type?._id || "");

        // Only append image if it's a new File (i.e., user uploaded/changed it)
        if (values?.image_url instanceof File) {
          formData.append("image", values.image_url);
        }
        addDesignBankData(formData).then((response) => {
          if (response?.data?.status_code === 200) {
            handleCloseModal();
            toast.success("Added successfully");
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.selectedItem = "";
              })
            );
            refetch();
            formik?.resetForm();
            formik?.setFieldValue("image_url", "");
          } else if (response?.status_code === 422) {
            handleBackendError(response);
          } else {
            toast.error("Something went wrong");
            handleBackendError(response);
          }
        });
      }
    },
  });

  useEffect(() => {
    if (queryState?.isEdit && typeof formik?.values?.image_url === "string") {
      setImagePreview(formik?.values?.image_url || "");
      return;
    }

    if (!queryState?.isEdit) {
      setImagePreview("");
    }
  }, [formik?.values?.image_url, queryState?.isEdit]);

  const handleCloseModal = () => {
    formik.resetForm();
    setImagePreview("");
    dispatch(
      updateConfig((state) => {
        state.showAddModal = false;
        state.itemData = [];
      })
    );
  };

  const handleBackendError = (response) => {
    if (response?.status_code === 422) {
      let errors = response?.errors;
      let errorField = Object.keys(errors);
      errorField.forEach((field) => {
        if (field === "supplier_id") {
          formik?.setFieldError("supplier", errors[field]);
        } else if (field === "item_id") {
          formik?.setFieldError("item_type", errors[field]);
        } else {
          formik?.setFieldError(field, errors[field]);
        }
      });
    }
  };

  const handleFirstImageChange = (e) => {
    formik.setFieldTouched("image_url", true);
    const image = e?.target?.files?.[0];
    if (!image) return;

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(image.type)) {
      formik.setFieldError("image_url", "Only PNG or JPG files are allowed.");
      return;
    }

    formik.setFieldValue("image_url", image);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result); // base64 string
    };
    reader.readAsDataURL(image);
  };

  const handleFirstImageDelete = () => {
    if (formik?.values?.image_url) {
      formik?.setFieldValue("image_url", "");
      setImagePreview("");
      dispatch(
        updateConfig((state) => {
          state.itemData = { ...state.itemData, image_url: "" };
        })
      );
    }
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  return {
    formik,
    queryState,
    basicData: basicData?.data,
    imagePreview,
    getFieldError,
    handleCloseModal,
    handleFirstImageChange,
    handleFirstImageDelete,
  };
};

export default useAddDesignBank;
